import { useState } from 'react'
import { logoBackgroundEnabled } from './logoBackground'
import './TenantLogo.css'

/**
 * How light a logo's own ink is, 0–1, read from its opaque pixels — or null when
 * the image cannot be read (cross-origin, not yet decoded).
 *
 * Sampled at 48px: enough to tell a white mark from a coloured one, cheap enough
 * to run on every load.
 */
function inkLightness(img) {
  try {
    const size = 48
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const context = canvas.getContext('2d', { willReadFrequently: true })
    context.drawImage(img, 0, 0, size, size)
    const { data } = context.getImageData(0, 0, size, size)
    let sum = 0
    let count = 0
    for (let index = 0; index < data.length; index += 4) {
      if (data[index + 3] < 128) continue
      sum += (0.2126 * data[index] + 0.7152 * data[index + 1] + 0.0722 * data[index + 2]) / 255
      count += 1
    }
    return count ? sum / count : null
  } catch {
    return null
  }
}

/**
 * The tenant's main logo.
 *
 * `background` is the tenant's LogoBackgroundEnabled setting: a white plate behind
 * the mark, for a logo drawn in colour that would be lost on the dark rail. A logo
 * drawn in WHITE does not need one — and on a white plate it disappears entirely,
 * which is what the default installation showed (the default mark is white and the
 * setting defaults to on). So the plate is drawn only when the setting asks for it
 * AND the mark is not itself light.
 */
export default function TenantLogo({ background, className = '', imageClassName, src = '/images/Rawam-logo-with-text2.png', alt = 'Rawam' }) {
  /* null until the mark has been measured. The plate waits for the answer rather
     than assuming one, so a white mark never flashes a white slab on load. */
  const [lightMark, setLightMark] = useState(null)
  const plate = logoBackgroundEnabled(background) && lightMark === false

  return (
    <span className={`tenant-logo${plate ? ' tenant-logo--plate' : ''} ${className}`}>
      <img
        className={imageClassName}
        src={src}
        alt={alt}
        onLoad={(event) => {
          const lightness = inkLightness(event.currentTarget)
          /* Unreadable (cross-origin) counts as "not light": the setting decides. */
          setLightMark(lightness != null && lightness > 0.85)
        }}
      />
    </span>
  )
}
